import Loader from '@shared/components/Loader';
import { VisitDetail } from '@features/visits/entity';
import { Card, CardContent, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAllPricingCalculatorsQuery } from '../queries';
import { useCurrency } from '@features/currency/currency.hooks';

type MonthlyTrendChartProps = {
  allVisits?: VisitDetail[];
};

export type CostPeriod = 'monthly' | 'yearly' | 'quarterly';

export type OverheadCost = {
  name: string;
  amount: number;
  period: CostPeriod;
  description?: string;
};

export type PricingCalculator = {
  id: string;
  userId: string;
  overheadCosts: OverheadCost[];
  calculationMonth: Date;
  createdAt: Date;
  updatedAt: Date;
};

const MonthlyTrendChart = ({ allVisits }: MonthlyTrendChartProps) => {
  const { data: calculators, isLoading } = useAllPricingCalculatorsQuery();
  const { preferredCurrency } = useCurrency();

  const chartData = useMemo(() => {
    if (!calculators || calculators.length === 0) {
      return null;
    }
    const sortedCalculators = [...calculators].sort(
      (a, b) => dayjs(a.calculationMonth).valueOf() - dayjs(b.calculationMonth).valueOf(),
    );
    const months = sortedCalculators.map(calc => dayjs(calc.calculationMonth).format('MMM YYYY'));

    const monthlyCosts = sortedCalculators.map(calc => {
      const total = calc.overheadCosts.reduce((sum, cost) => {
        let monthlyAmount = cost.amount;
        if (cost.period === 'yearly') {
          monthlyAmount = cost.amount / 12;
        }
        if (cost.period === 'quarterly') {
          monthlyAmount = cost.amount / 3;
        }
        return sum + monthlyAmount;
      }, 0);
      return Math.round(total);
    });

    const actualRevenue = sortedCalculators.map(calc => {
      if (!allVisits || allVisits.length === 0) {
        return 0;
      }
      // Calculate revenue for this month
      const calcMonth = dayjs(calc.calculationMonth);
      const monthStart = calcMonth.startOf('month');
      const monthEnd = calcMonth.endOf('month');

      // Filter visits for this month
      const monthVisits = allVisits.filter(({ visit }) => {
        const visitDate = dayjs(visit.dateFrom);
        return visitDate.isAfter(monthStart) && visitDate.isBefore(monthEnd);
      });

      // Calculate total revenue from all visits in this month
      const totalRevenue = monthVisits.reduce((sum, { loyalty, financials }) => {
        return (
          sum +
          (Number(financials.paidPrice) || 0) +
          Number(financials.totalSalesRevenue || 0) -
          Number(loyalty.loyaltyPointsUsed || 0)
        );
      }, 0);

      return Math.round(totalRevenue);
    });

    const variableCosts = sortedCalculators.map(calc => {
      if (!allVisits || allVisits.length === 0) {
        return 0;
      }

      // Calculate variable costs for this month
      const calcMonth = dayjs(calc.calculationMonth);
      const monthStart = calcMonth.startOf('month');
      const monthEnd = calcMonth.endOf('month');

      // Filter visits for this month
      const monthVisits = allVisits.filter(({ visit }) => {
        const visitDate = dayjs(visit.dateFrom);
        return visitDate.isAfter(monthStart) && visitDate.isBefore(monthEnd);
      });

      // Calculate total variable costs from all visits in this month
      let totalVariableCost = 0;

      monthVisits.forEach(({ procedures }) => {
        // Access procedures at visit level
        if (procedures.length) {
          procedures.forEach(procedure => {
            if (procedure.stockAllowances.length) {
              const procedureCost = procedure.stockAllowances.reduce((acc, allowance) => {
                return acc + Number(allowance.costs || 0);
              }, 0);
              totalVariableCost += procedureCost;
            }
          });
        }
      });

      return Math.round(totalVariableCost);
    });

    const actualProfit = sortedCalculators.map((calc, index) => {
      const revenue = actualRevenue[index];
      const fixedCosts = monthlyCosts[index];
      const varCosts = variableCosts[index];

      return revenue - fixedCosts - varCosts;
    });

    return {
      months,
      monthlyCosts,
      variableCosts,
      actualRevenue,
      actualProfit,
    };
  }, [calculators, allVisits]);

  if (isLoading) {
    return <Loader />;
  }

  const hasVariableCosts = allVisits && allVisits.length > 0;

  return (
    <>
      {chartData && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FormattedMessage
                id="pricing.analytics.revenueTrend"
                defaultMessage="Vývoj příjmů, nákladů a zisku"
              />
            </Typography>
            {!hasVariableCosts && (
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                <FormattedMessage
                  id="pricing.analytics.noVariableCosts"
                  defaultMessage="Poznámka: Náklady na materiál nejsou k dispozici pro historická data"
                />
              </Typography>
            )}
            <LineChart
              xAxis={[
                {
                  scaleType: 'point',
                  data: chartData.months,
                  label: 'Měsíc',
                },
              ]}
              series={[
                {
                  data: chartData.actualRevenue,
                  label: `Skutečný příjem (${preferredCurrency})`,
                  color: '#4caf50',
                  curve: 'natural',
                },
                {
                  data: chartData.monthlyCosts,
                  label: `Stálé náklady (${preferredCurrency})`,
                  color: '#f44336',
                  curve: 'natural',
                },
                {
                  data: chartData.variableCosts,
                  label: `Náklady materiál (${preferredCurrency})`,
                  color: '#ff9800',
                  curve: 'natural',
                },
                {
                  data: chartData.actualProfit,
                  label: `Čistý zisk (${preferredCurrency})`,
                  color: '#2196f3',
                  curve: 'natural',
                },
              ]}
              height={350}
              grid={{ vertical: true, horizontal: true }}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default MonthlyTrendChart;
